import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, EnvelopeSimple, FileText, Fingerprint, PencilSimple, Sparkle } from "@phosphor-icons/react";
import ProductPageShell from "@/components/ProductPageShell";
import SeoContextFooter from "@/components/SeoContextFooter";
import crmNotesEditorialDesktop from "@/assets/crm-notes-editorial-1600.webp";
import crmNotesEditorialMobile from "@/assets/crm-notes-editorial-1000.webp";
import { trackPublicEvent } from "@/lib/publicAnalytics";

const source = "product_crm_notes";
const writingLayers = [
  {
    icon: FileText,
    label: "Conversation evidence",
    title: "Start with what actually happened.",
    copy: "Both the CRM note and the follow-up are grounded in the customer conversation, so the facts, blockers, decisions, and next steps stay aligned.",
  },
  {
    icon: Fingerprint,
    label: "Relationship context",
    title: "Carry forward what came before.",
    copy: "Callsect interprets the current call alongside the relationship context that matters, instead of treating every conversation like an isolated transcript.",
  },
  {
    icon: PencilSimple,
    label: "Writing DNA",
    title: "Keep the output recognizably yours.",
    copy: "Stable patterns in your structure, detail, tone, and decision language shape the result—whether it is going into the CRM or back to the customer.",
  },
];

const previewContent = {
  note: {
    eyebrow: "CRM note",
    title: "Ready to save. Still sounds like a person.",
    body: (
      <>
        <p><strong>Spoke with Dana ahead of the renewal.</strong> She was candid that the team likes the platform, but adoption is too low to justify another year: Operations uses it, Finance barely logs in, and Sales dropped off after the first month.</p>
        <p>The real blocker emerged when Dana explained that Priya, who owned the rollout, left in May. Dana inherited it without the bandwidth or authority to drive adoption. The CFO needs measurable value, not positive sentiment.</p>
        <p>I will send a one-page value snapshot early Friday using current usage data, and we booked an ROI workshop for Monday at 2pm with Dana and Operations.</p>
      </>
    ),
  },
  followup: {
    eyebrow: "Follow-up",
    title: "Ready to send. Same conversation, different job.",
    body: (
      <>
        <p><strong>Subject: Usage snapshot before Monday</strong></p>
        <p>Hi Dana, thanks again for being so open about where things stand. I took away that the issue is less about whether the team sees value and more about whether there is enough adoption to make the renewal easy to justify internally.</p>
        <p>I’ll send the one-page usage snapshot on Friday so you have something concrete for the CFO. Then on Monday at 2pm we can use the workshop with Operations to look at where adoption dropped off and what would need to change.</p>
        <p>Speak Monday,<br />Maurizio</p>
      </>
    ),
  },
};

function track(section, ctaId) {
  trackPublicEvent("cta_click", { source, section, cta_id: ctaId });
}

export default function ProductCrmNotesPage() {
  const [previewMode, setPreviewMode] = useState("note");
  const preview = previewContent[previewMode];

  const handlePreviewTabKeyDown = (event) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    const tabs = Array.from(event.currentTarget.parentElement?.querySelectorAll('[role="tab"]') || []);
    if (!tabs.length) return;
    const currentIndex = Math.max(0, tabs.indexOf(event.currentTarget));
    let nextIndex = currentIndex;

    if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = tabs.length - 1;

    event.preventDefault();
    tabs[nextIndex]?.focus();
    tabs[nextIndex]?.click();
  };

  return (
    <ProductPageShell source={source}>
      <section className="product-feature-hero product-feature-hero--crm-notes px-5 pb-16 pt-16 sm:px-8 sm:pb-24 sm:pt-28">
        <div className="product-crm-notes-art pointer-events-none absolute inset-0" aria-hidden="true">
          <picture className="product-crm-notes-art__picture">
            <source media="(max-width: 600px)" srcSet={crmNotesEditorialMobile} />
            <img
              className="product-crm-notes-art__image"
              src={crmNotesEditorialDesktop}
              alt=""
              loading="eager"
              decoding="async"
            />
          </picture>
        </div>

        <div className="product-feature-hero__layout relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.94fr_1.06fr] lg:items-start lg:gap-16">
          <div className="product-feature-hero__copy">
            <h1 className="product-feature-hero__title landing-hero-title max-w-3xl text-[2.7rem] font-semibold leading-[1.01] tracking-[-0.055em] sm:text-6xl lg:text-7xl">
              <span className="product-feature-hero__title-base block">Write it down. Send it out.</span>
              <span className="product-feature-hero__title-accent landing-hero-spectrum landing-hero-promise block">Still sound like you.</span>
            </h1>
            <p className="product-feature-hero-subcopy landing-hero-subcopy marketing-lead-copy mt-7 max-w-2xl">
              Callsect turns the same customer conversation into CRM-ready documentation and a follow-up ready to send, using the facts that matter, relationship context, and the writing patterns you already use.
            </p>
          </div>

          <article className="product-crm-note-preview">
            <div className="product-crm-note-preview__head">
              <div className="product-writing-preview__tabs" role="tablist" aria-label="Writing output preview">
                <button
                  id="writing-preview-tab-note"
                  type="button"
                  role="tab"
                  aria-selected={previewMode === "note"}
                  aria-controls="writing-output-preview-panel"
                  tabIndex={previewMode === "note" ? 0 : -1}
                  className={`product-writing-preview__tab ${previewMode === "note" ? "is-active" : ""}`}
                  onClick={() => setPreviewMode("note")}
                  onKeyDown={handlePreviewTabKeyDown}
                >
                  <FileText size={16} weight="duotone" aria-hidden="true" /> CRM note
                </button>
                <button
                  id="writing-preview-tab-followup"
                  type="button"
                  role="tab"
                  aria-selected={previewMode === "followup"}
                  aria-controls="writing-output-preview-panel"
                  tabIndex={previewMode === "followup" ? 0 : -1}
                  className={`product-writing-preview__tab ${previewMode === "followup" ? "is-active" : ""}`}
                  onClick={() => setPreviewMode("followup")}
                  onKeyDown={handlePreviewTabKeyDown}
                >
                  <EnvelopeSimple size={16} weight="duotone" aria-hidden="true" /> Follow-up
                </button>
              </div>
              <strong>{preview.title}</strong>
            </div>
            <div
              id="writing-output-preview-panel"
              className="product-crm-note-preview__body"
              role="tabpanel"
              aria-labelledby={`writing-preview-tab-${previewMode}`}
              aria-live="polite"
              data-output={preview.eyebrow}
            >
              {preview.body}
            </div>
          </article>
        </div>

        <div className="product-feature-hero__actions mx-auto flex flex-col gap-3 sm:flex-row">
          <Link to="/login?mode=register" onClick={() => track("hero", "sign_up_free")} className="product-feature-hero-primary landing-signup-primary-cta inline-flex items-center justify-center rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition">
            Sign up free
          </Link>
          <Link to="/demo" onClick={() => track("hero", "sales_demo")} className="product-feature-hero-secondary landing-hero-secondary-cta inline-flex items-center justify-center gap-2 rounded-xl marketing-secondary-action border px-5 py-3.5 font-medium marketing-text-secondary-strong transition">
            See Demo
            <ArrowRight size={17} weight="bold" />
          </Link>
        </div>
      </section>

      <section className="product-feature-section marketing-band border-y px-5 py-16 sm:px-8 sm:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="product-feature-section__intro product-feature-section__intro--no-kicker">
            <h2>Different destination. Same understanding. Same voice.</h2>
          </div>
          <div className="product-feature-grid product-feature-grid--three mt-12">
            {writingLayers.map(({ icon: Icon, label, title, copy }) => (
              <article className="product-feature-card" key={label}>
                <Icon size={24} weight="duotone" aria-hidden="true" />
                <span>{label}</span>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="product-feature-section px-5 py-16 sm:px-8 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-start lg:gap-16">
          <div className="product-feature-sticky">
            <span className="product-feature-kicker">Writing DNA</span>
            <h2>Your style is more than a tone setting.</h2>
            <p className="marketing-lead-copy">
              Callsect learns stable patterns from the writing examples you provide: structure, level of detail, tone, sequencing, and the way you explain decisions. It uses those patterns to shape both internal notes and customer-facing follow-ups without turning your style into a caricature.
            </p>
          </div>

          <div className="product-writing-dna">
            <article>
              <span>Structure</span>
              <strong>How you organize context, blockers, decisions, and next steps.</strong>
            </article>
            <article>
              <span>Detail</span>
              <strong>How much evidence and explanation you normally keep.</strong>
            </article>
            <article>
              <span>Tone</span>
              <strong>How direct, conversational, or formal your writing tends to be.</strong>
            </article>
            <article>
              <span>Decision language</span>
              <strong>How you explain what changed, what matters, and what happens next.</strong>
            </article>
          </div>
        </div>
      </section>

      <section className="product-feature-band px-5 py-14 sm:px-8 sm:py-16">
        <div className="marketing-highlight-band marketing-highlight-band--with-context mx-auto flex max-w-6xl flex-col gap-6 rounded-2xl border p-7 sm:p-9 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <Sparkle size={25} weight="duotone" aria-hidden="true" />
            <h2>Document it. Send it. Keep the relationship.</h2>
          </div>
          <Link to="/product/relationship-memory" className="product-feature-inline-link">
            See relationship memory
            <ArrowRight size={17} weight="bold" />
          </Link>
          <SeoContextFooter
            label="Related guide"
            to="/crm-notes-after-sales-call"
            onClick={() => track("internal_link", "crm_notes_after_sales_call")}
          >
            CRM notes after a sales call
          </SeoContextFooter>
        </div>
      </section>
    </ProductPageShell>
  );
}
